import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Users, Search, Eye, Shield, Building2, User,
  UserPlus, X, Loader2, CheckCircle, ChevronDown, Clipboard, Settings
} from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import { MUNICIPIOS } from '../../utils/cidades';

// ─── Modal Criar Usuário ──────────────────────────────────────
const CANDIDATO_VAZIO = {
  nome: '', email: '', senha: '', cidade: '', telefone: '', whatsapp: '',
};
const EMPRESA_VAZIA = {
  nome: '', email: '', senha: '',
  razao_social: '', nome_fantasia: '', cnpj: '',
  setor: '', porte: 'pequena', cidade: '',
  telefone_comercial: '', email_comercial: '',
};

const SETORES = [
  'Tecnologia', 'Comércio', 'Saúde', 'Educação', 'Indústria',
  'Agronegócio', 'Construção', 'Serviços', 'Financeiro', 'Outro',
];

function ModalCriarUsuario({ onFechar, onCriado }) {
  const [tipo, setTipo]       = useState('candidato');
  const [form, setForm]       = useState(CANDIDATO_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);

  // Troca o formulário ao mudar tipo
  const handleTipo = (t) => {
    setTipo(t);
    setForm(t === 'candidato' ? CANDIDATO_VAZIO : EMPRESA_VAZIA);
  };

  const set = (campo, val) => setForm(p => ({ ...p, [campo]: val }));

  const salvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    try {
      const payload = { tipo, ...form };
      const { data } = await api.post('/admin/usuarios/criar', payload);
      toast.success(data.mensagem || 'Usuário criado!');
      onCriado();
      onFechar();
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao criar usuário.');
    }
    setSalvando(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onFechar} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-acre-dark to-acre-green p-5 rounded-t-2xl flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center">
              <UserPlus size={18} className="text-white" />
            </div>
            <div>
              <p className="font-sora font-bold text-white">Criar Perfil de Teste</p>
              <p className="text-white/60 text-xs">Cadastro direto pelo administrador</p>
            </div>
          </div>
          <button onClick={onFechar} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors">
            <X size={15} />
          </button>
        </div>

        <form onSubmit={salvar} className="p-5 space-y-4">
          {/* Seletor de tipo */}
          <div>
            <label className="label mb-2">Tipo de perfil</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: 'candidato', l: '👤 Candidato', desc: 'Perfil com currículo' },
                { v: 'empresa',   l: '🏢 Empresa',   desc: 'Perfil com acesso ativo' },
              ].map(t => (
                <button key={t.v} type="button" onClick={() => handleTipo(t.v)}
                  className={`p-3 rounded-xl border-2 text-left transition-all ${
                    tipo === t.v
                      ? 'border-acre-green bg-acre-light'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <p className={`font-sora font-semibold text-sm ${tipo === t.v ? 'text-acre-green' : 'text-gray-700'}`}>{t.l}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
                </button>
              ))}
            </div>
          </div>

          {/* ── Campos comuns ── */}
          <div className="space-y-3">
            <div>
              <label className="label">Nome completo *</label>
              <input className="input" required value={form.nome} onChange={e => set('nome', e.target.value)} placeholder="Ex: João Silva" />
            </div>
            <div>
              <label className="label">E-mail *</label>
              <input className="input" type="email" required value={form.email} onChange={e => set('email', e.target.value)} placeholder="joao@email.com" />
            </div>
            <div>
              <label className="label">Senha * (mín. 8 caracteres)</label>
              <div className="relative">
                <input
                  className="input pr-16"
                  type={mostrarSenha ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={form.senha}
                  onChange={e => set('senha', e.target.value)}
                  placeholder="Mínimo 8 caracteres"
                />
                <button type="button" onClick={() => setMostrarSenha(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-acre-green font-medium transition-colors">
                  {mostrarSenha ? 'Ocultar' : 'Mostrar'}
                </button>
              </div>
            </div>
          </div>

          {/* ── Campos exclusivos de candidato ── */}
          {tipo === 'candidato' && (
            <div className="space-y-3 pt-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dados do candidato</p>
              <div>
                <label className="label">Cidade *</label>
                <select className="input" required value={form.cidade} onChange={e => set('cidade', e.target.value)}>
                  <option value="">Selecione...</option>
                  {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">WhatsApp</label>
                  <input className="input" value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="68 9 9999-9999" />
                </div>
                <div>
                  <label className="label">Telefone</label>
                  <input className="input" value={form.telefone} onChange={e => set('telefone', e.target.value)} placeholder="68 3333-3333" />
                </div>
              </div>
            </div>
          )}

          {/* ── Campos exclusivos de empresa ── */}
          {tipo === 'empresa' && (
            <div className="space-y-3 pt-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Dados da empresa</p>
              <div>
                <label className="label">Razão social *</label>
                <input className="input" required value={form.razao_social} onChange={e => set('razao_social', e.target.value)} placeholder="Ex: Empresa ABC Ltda" />
              </div>
              <div>
                <label className="label">Nome fantasia</label>
                <input className="input" value={form.nome_fantasia} onChange={e => set('nome_fantasia', e.target.value)} placeholder="Ex: ABC Soluções" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">CNPJ</label>
                  <input className="input" value={form.cnpj} onChange={e => set('cnpj', e.target.value)} placeholder="00.000.000/0000-00" />
                </div>
                <div>
                  <label className="label">Porte</label>
                  <select className="input" value={form.porte} onChange={e => set('porte', e.target.value)}>
                    <option value="micro">Micro</option>
                    <option value="pequena">Pequena</option>
                    <option value="media">Média</option>
                    <option value="grande">Grande</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Setor</label>
                  <select className="input" value={form.setor} onChange={e => set('setor', e.target.value)}>
                    <option value="">Selecione...</option>
                    {SETORES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Cidade</label>
                  <select className="input" value={form.cidade} onChange={e => set('cidade', e.target.value)}>
                    <option value="">Selecione...</option>
                    {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Telefone comercial</label>
                  <input className="input" value={form.telefone_comercial} onChange={e => set('telefone_comercial', e.target.value)} placeholder="68 3333-3333" />
                </div>
                <div>
                  <label className="label">E-mail comercial</label>
                  <input className="input" type="email" value={form.email_comercial} onChange={e => set('email_comercial', e.target.value)} placeholder="contato@empresa.com" />
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 flex items-start gap-2">
                <CheckCircle size={13} className="flex-shrink-0 mt-0.5 text-blue-500" />
                <span>Empresa criada pelo admin terá acesso <strong>ativo por 1 ano</strong> automaticamente, sem necessidade de aprovação de pagamento.</span>
              </div>
            </div>
          )}

          {/* Ações */}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onFechar} className="btn-outline flex-1 justify-center py-2.5 text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={salvando} className="btn-primary flex-1 justify-center py-2.5 text-sm flex items-center gap-2">
              {salvando
                ? <><Loader2 size={14} className="animate-spin" /> Criando...</>
                : <><CheckCircle size={14} /> Criar {tipo === 'candidato' ? 'Candidato' : 'Empresa'}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Cards de totais gerais (busca sem filtro) ────────────────
function TotaisGerais({ filtroTipo, setFiltroTipo }) {
  const [totais, setTotais] = useState({ candidatos: 0, empresas: 0, admins: 0 });

  useEffect(() => {
    Promise.all([
      api.get('/admin/usuarios?tipo=candidato&limit=1'),
      api.get('/admin/usuarios?tipo=empresa&limit=1'),
      api.get('/admin/usuarios?tipo=admin&limit=1'),
    ]).then(([c, e, a]) => {
      setTotais({
        candidatos: c.data.total || 0,
        empresas:   e.data.total || 0,
        admins:     a.data.total || 0,
      });
    }).catch(() => {});
  }, []);

  return (
    <div className="grid grid-cols-3 gap-3 mb-5">
      {[
        { label:'Candidatos', tipo:'candidato', cor:'text-emerald-600', bg:'bg-emerald-50', n: totais.candidatos },
        { label:'Empresas',   tipo:'empresa',   cor:'text-amber-600',   bg:'bg-amber-50',   n: totais.empresas   },
        { label:'Admins',     tipo:'admin',     cor:'text-purple-600',  bg:'bg-purple-50',  n: totais.admins     },
      ].map(c => (
        <button key={c.label} onClick={() => setFiltroTipo(filtroTipo===c.tipo?'':c.tipo)}
          className={`${c.bg} rounded-xl p-3 text-center transition-all ${filtroTipo===c.tipo?'ring-2 ring-offset-1 ring-acre-green':''}`}>
          <p className={`font-sora font-bold text-2xl ${c.cor}`}>{c.n}</p>
          <p className="text-gray-500 text-xs">{c.label}</p>
        </button>
      ))}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────
export default function AdminUsuarios() {
  const [usuarios,    setUsuarios]    = useState([]);
  const [total,       setTotal]       = useState(0);  // total filtrado
  const [totalGeral,  setTotalGeral]  = useState(0);  // total sem filtro
  const [busca,       setBusca]       = useState('');
  const [filtroTipo,  setFiltroTipo]  = useState('');
  const [filtroCidade,setFiltroCidade]= useState('');
  const [ordem,       setOrdem]       = useState('recentes');
  const [pagina,      setPagina]      = useState(1);
  const [loading,     setLoading]     = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const LIMIT = 50;
  const navigate = useNavigate();

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagina, limit: LIMIT, ordem });
      if (busca)        params.set('busca',  busca);
      if (filtroTipo)   params.set('tipo',   filtroTipo);
      if (filtroCidade) params.set('cidade', filtroCidade);

      const [resLista, resTotal] = await Promise.all([
        api.get(`/admin/usuarios?${params}`),
        // Total geral sem filtros (só na primeira carga ou quando não há filtros)
        totalGeral === 0 ? api.get('/admin/usuarios?limit=1') : Promise.resolve(null),
      ]);

      setUsuarios(resLista.data.usuarios || []);
      setTotal(resLista.data.total || 0);
      if (resTotal) setTotalGeral(resTotal.data.total || 0);

    } catch { setUsuarios([]); }
    setLoading(false);
  }, [busca, filtroTipo, filtroCidade, ordem, pagina, totalGeral]);

  useEffect(() => { setPagina(1); }, [busca, filtroTipo, filtroCidade, ordem]);
  useEffect(() => { carregar(); }, [carregar]);

  const TIPO_ICON = {
    admin:     <Shield size={12}/>,
    empresa:   <Building2 size={12}/>,
    candidato: <User size={12}/>,
  };
  const TIPO_COR = {
    admin:     'bg-purple-100 text-purple-700',
    empresa:   'bg-amber-100 text-amber-700',
    candidato: 'bg-emerald-100 text-emerald-700',
  };
  const STATUS_COR = {
    ativo:                  'bg-emerald-100 text-emerald-700',
    pendente:               'bg-yellow-100 text-yellow-700',
    expirado:               'bg-red-100 text-red-700',
    bloqueado:              'bg-gray-100 text-gray-500',
    aguardando_aprovacao:   'bg-blue-100 text-blue-700',
  };

  const totalPaginas = Math.ceil(total / LIMIT);

  return (
    <Layout title="Usuários"
      subtitle={`${totalGeral || total} usuário(s) cadastrado(s) no total${(busca||filtroTipo||filtroCidade) ? ` · ${total} encontrado(s) no filtro` : ''}`}>
      {modalAberto && <ModalCriarUsuario onFechar={() => setModalAberto(false)} onCriado={carregar}/>}

      {/* ── Filtros ── */}
      <div className="card p-4 mb-5 space-y-3">
        {/* Linha 1: busca + tipo + criar */}
        <div className="flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-56">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400"/>
            <input value={busca} onChange={e => setBusca(e.target.value)}
              placeholder="Buscar por nome ou e-mail..."
              className="input pl-9 w-full"/>
          </div>
          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} className="input w-40">
            <option value="">Todos os tipos</option>
            <option value="candidato">👤 Candidatos</option>
            <option value="empresa">🏢 Empresas</option>
            <option value="admin">🛡️ Admins</option>
          </select>
          <button onClick={() => setModalAberto(true)}
            className="btn-primary flex items-center gap-2 px-4 py-2 text-sm flex-shrink-0">
            <UserPlus size={15}/> Criar Perfil
          </button>
          <Link to="/admin/configuracoes"
            className="btn-outline flex items-center gap-2 px-4 py-2 text-sm flex-shrink-0">
            <Settings size={15}/> Config
          </Link>
        </div>

        {/* Linha 2: cidade + ordem */}
        <div className="flex gap-3 flex-wrap">
          <select value={filtroCidade} onChange={e => setFiltroCidade(e.target.value)} className="input flex-1 min-w-44">
            <option value="">📍 Todas as cidades</option>
            {MUNICIPIOS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={ordem} onChange={e => setOrdem(e.target.value)} className="input w-52">
            <option value="recentes">📅 Mais recentes</option>
            <option value="antigos">📅 Mais antigos</option>
            <option value="nome_az">🔤 Nome A → Z</option>
            <option value="nome_za">🔤 Nome Z → A</option>
            <option value="cidade">📍 Cidade A → Z</option>
            <option value="ultimo_login">🕐 Último acesso</option>
          </select>
          {(busca || filtroTipo || filtroCidade || ordem !== 'recentes') && (
            <button onClick={() => { setBusca(''); setFiltroTipo(''); setFiltroCidade(''); setOrdem('recentes'); }}
              className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 transition-colors px-2">
              <X size={12}/> Limpar filtros
            </button>
          )}
        </div>
      </div>

      {/* ── Cards resumo — buscam total geral (sem filtro) ── */}
      <TotaisGerais filtroTipo={filtroTipo} setFiltroTipo={setFiltroTipo}/>

      {/* ── Tabela ── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={28} className="animate-spin text-acre-green"/>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Usuário</th>
                <th className="px-4 py-3 text-left hidden sm:table-cell">Tipo</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Cidade</th>
                <th className="px-4 py-3 text-left hidden md:table-cell">Status</th>
                <th className="px-4 py-3 text-left hidden lg:table-cell">Cadastro</th>
                <th className="px-4 py-3 text-center">Perfil</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {usuarios.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12">
                    <Users size={32} className="text-gray-200 mx-auto mb-2"/>
                    <p className="text-gray-400 text-sm">Nenhum usuário encontrado.</p>
                  </td>
                </tr>
              ) : usuarios.map(u => (
                <tr key={u.id} className="hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => u.tipo !== 'admin' && navigate(`/admin/${u.tipo==='candidato'?'candidatos':'empresas'}/${u.id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm text-white flex-shrink-0
                        ${u.tipo==='empresa'?'bg-amber-500':u.tipo==='admin'?'bg-purple-600':'bg-emerald-500'}`}>
                        {u.nome?.[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-acre-dark truncate max-w-[180px]">{u.nome}</p>
                        <p className="text-gray-400 text-xs truncate max-w-[180px]">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full ${TIPO_COR[u.tipo]}`}>
                      {TIPO_ICON[u.tipo]} {u.tipo}
                    </span>
                    {u.tipo === 'candidato' && u.porcentagem_perfil > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">{u.porcentagem_perfil}% perfil</p>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-xs text-gray-500">
                    {u.cidade || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${STATUS_COR[u.status_pagamento]||'bg-gray-100 text-gray-500'}`}>
                      {u.status_pagamento?.replace(/_/g,' ')||'—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-gray-400 text-xs">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                    {u.tipo !== 'admin' ? (
                      <button
                        onClick={() => navigate(`/admin/${u.tipo==='candidato'?'candidatos':'empresas'}/${u.id}`)}
                        className="inline-flex items-center gap-1 text-xs bg-acre-green text-white px-2.5 py-1.5 rounded-lg hover:bg-acre-mid transition-colors">
                        <Eye size={12}/> Ver perfil
                      </button>
                    ) : (
                      <span className="text-xs text-gray-300">Admin</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Paginação ── */}
      {totalPaginas > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-gray-400">
            Mostrando {((pagina-1)*LIMIT)+1}–{Math.min(pagina*LIMIT, total)} de <strong>{total}</strong> usuários
          </p>
          <div className="flex items-center gap-2">
            <button disabled={pagina===1} onClick={() => setPagina(p=>p-1)}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:border-acre-green transition-colors">
              ← Anterior
            </button>
            <span className="text-xs text-gray-500 px-2">
              {pagina} / {totalPaginas}
            </span>
            <button disabled={pagina>=totalPaginas} onClick={() => setPagina(p=>p+1)}
              className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:border-acre-green transition-colors">
              Próxima →
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}
